import axios from 'axios';
import { createDocEntryStore } from './docStore';
import { readState, writeState } from './docState';
import { atWords, isEntry, type FloorSpot, type Placement } from './covers';
import type { HardwareInteraction } from './hardwareInteractionsRun';
import { checkCovers } from './jointCheck';
import { ASTRA_MODEL } from './models';
import { namedIn, shownId, withLetter } from './partsCatalogue';
import { isHardwareOrTool, isLeftRight, type PartItem } from './partsRun';
import { holeWidthOf, type StepHole } from './stepHoles';
import type { ByPart } from './partModelSize';

// PLACE THE COVERS — a model call, one pass (see cover_placement.py for the question), ONE JOINT AT
// A TIME: its covers, its hardware table rows, and its places — the holes Find holes found in it,
// and where its parts touch the floor. A cover only ever concerns its own hole or foot. The answer
// is placed on the model at once — the joint view draws it, and the collision check measures it.

// ASKED OF GPT-6 ASTRA: a question of reading places off words, the kind this app moved to Astra
// everywhere it compared the two. One name, for the run and for the badge's previews alike.
export const COVERS_MODEL = ASTRA_MODEL;

// WHAT THE CALL IS TOLD, as the server words it. Keys, not stored ids, name the covers: the id a
// person reads ("11"), which the answer echoes back.
export type CoverFacts = {
  covers: { key: string; id: string; name: string; description: string; qty: number | null; size: number[] | null }[];
  parts: { name: string; hands: boolean }[];
  // `both`: the row stands for a mirror pair's two, one per hand.
  rows: { hardware: string[]; covers: string[]; parts: string[]; how: string; both: boolean }[];
  holes: {
    id: string; partName: string; hand?: string; face: string; u: string; v: string;
    hardware: string | null; entry: boolean; through: boolean;
    mouthMm: number | null; boreMm: number | null; recessMm: number | null;
  }[];
  spots: { id: string; partName: string; hand?: string; face: string; u: string; v: string }[];
};

type Pass = { reasoning: string; raw: string; notes: string };
// ONE JOINT'S ANSWER.
export type JointCovers = {
  placements: Placement[] | null;
  passes: Pass[];
  // HOW DEEP EACH COVER GOES INTO A PART, by cover id, as the joint's scene last measured these
  // placements (see coverCollisions) — kept so the joint's tab counts them without being opened.
  // Absent: not measured yet. Gone with the placements it was measured on.
  into?: Record<string, { part: string; mm: number }[]>;
  // AND THE COVERS NOT DRAWN FOR WANT OF THE WOOD UNDER THEM — moved off their hole, or on a foot,
  // where a ray from where they stand met none (see JointHoles' mouths) — by drawn cover id, as
  // the same scene found it. Absent: not measured yet.
  missed?: string[];
};
type Snapshot = {
  // By joint id (see useJoints).
  byJoint: Record<string, JointCovers>;
  // The joint being placed.
  busy: string | null;
  error: string | null;
};
type Stored = Record<string, {
  placements?: Placement[]; passes?: Pass[]; into?: JointCovers['into']; missed?: string[];
}>;

// A NEW KEY: the answers under `notice.coverPlacements` were the whole product's, one call for
// every cover, and are not any joint's.
const key = (docId: string) => `notice.coverPlacementsByJoint.${docId}`;
const store = createDocEntryStore<Snapshot>((docId) => {
  const got = readState<Stored | null>(docId, key(docId), null) ?? {};
  return {
    snapshot: {
      byJoint: Object.fromEntries(Object.entries(got).map(([id, one]) => [id, {
        placements: one.placements ?? null,
        passes: one.passes ?? [],
        ...(one.into ? { into: one.into } : {}),
        ...(one.missed ? { missed: one.missed } : {}),
      }])),
      busy: null, error: null,
    },
  };
});

export const useCoverPlacements = store.useSnapshot;
export const NO_COVERS: JointCovers = { placements: null, passes: [] };

// EVERY JOINT'S ANSWER AS IT STANDS, and put back — what Stop restores (see JointsRun).
export const heldCovers = (docId: string) => store.get(docId).byJoint;
export function keepCovers(docId: string, byJoint: Record<string, JointCovers>) {
  store.patch(docId, { byJoint });
  save(docId);
}

const save = (docId: string) => {
  writeState(docId, key(docId), Object.fromEntries(Object.entries(store.get(docId).byJoint)
    .map(([id, { placements, passes, into, missed }]) => [id, {
      placements, passes, ...(into ? { into } : {}), ...(missed ? { missed } : {}),
    }])));
};
// New placements drop the depths measured on the old ones.
const patchJoint = (docId: string, joint: string, patch: Partial<JointCovers>) => {
  const { byJoint } = store.get(docId);
  const was = byJoint[joint] ?? NO_COVERS;
  const next = { ...was, ...patch };
  if ('placements' in patch && patch.placements !== was.placements) {
    if (!('into' in patch)) delete next.into;
    if (!('missed' in patch)) delete next.missed;
  }
  store.patch(docId, { byJoint: { ...byJoint, [joint]: next } });
};

// THE COVERS ITS SCENE FOUND NO WOOD UNDER, kept the same way as the depths below.
export function keepCoverMissed(docId: string, joint: string, placements: Placement[], missed: string[]) {
  const was = store.get(docId).byJoint[joint];
  if (!was || was.placements !== placements || JSON.stringify(was.missed) === JSON.stringify(missed)) return;
  patchJoint(docId, joint, { missed });
  save(docId);
}

// THE DEPTHS THE JOINT'S SCENE MEASURED, kept where they changed — on the placements they were
// measured on, and only those.
export function keepCoverInto(docId: string, joint: string, placements: Placement[], into: NonNullable<JointCovers['into']>) {
  const was = store.get(docId).byJoint[joint];
  if (!was || was.placements !== placements || JSON.stringify(was.into) === JSON.stringify(into)) return;
  patchJoint(docId, joint, { into });
  save(docId);
}

// THE FACTS OF ONE JOINT, off what the page holds: its rows (touchRows'), the parts they join,
// the holes found in it and the floor under its parts.
export function coverFacts(
  items: PartItem[],
  rows: HardwareInteraction[],
  holes: StepHole[],
  spots: FloorSpot[],
  // What each part's 3D model measures, by part id — a cover's size, and a bore's width.
  models: ByPart,
): CoverFacts {
  const name = (id: string) => {
    const p = items[Number(id)];
    return p ? `${shownId(id)} - ${withLetter(namedIn(p, 'en') || `#${id}`, p.letter)}` : `#${id}`;
  };
  const hand = (side?: 'left' | 'right') => (side ? { hand: side } : {});
  const covering = rows.filter((r) => r.covers.length);
  const covers = [...new Set(covering.flatMap((r) => r.covers))];
  // ONLY THE HOLES A COVER COULD GO ON: where a fastener it covers goes in and on through a part —
  // its head stays outside, which is what a cap caps. Not where one ends, nor a barrel nut's hole
  // across it: a page that sent every hole put buttons on those.
  const capped = new Set(covering.flatMap((r) => r.hardware.filter((h) => !r.covers.includes(h))));
  const capping = holes.filter((h) => isEntry(h) && h.through && h.via != null && capped.has(h.via));
  // A MIRROR PAIR'S TWO ROWS ARE ONE: the same pieces on the same parts, said once for both hands.
  const sameAs = (a: HardwareInteraction, b: HardwareInteraction) => [a.hardware, a.covers, a.components]
    .every((list, k) => JSON.stringify([...list].sort()) === JSON.stringify([...[b.hardware, b.covers, b.components][k]].sort()));
  const merged = covering.filter((r, k) => covering.findIndex((o) => sameAs(o, r)) === k)
    .map((r) => ({ r, both: covering.filter((o) => sameAs(o, r)).length > 1 }));
  const parts = [...new Set(rows.flatMap((r) => r.components))]
    .filter((id) => items[Number(id)] && !isHardwareOrTool(items[Number(id)]));
  return {
    // HOW MANY THIS JOINT TAKES, off its rows — not the quantity printed for the whole product.
    covers: covers.map((id) => ({
      key: shownId(id), id, name: name(id),
      description: items[Number(id)]?.description ?? '', qty: checkCovers(rows, null).find((c) => c.id === id)?.expected ?? null,
      // ITS SIZE, off its own 3D model — what decides whether it goes into a hole or onto it.
      size: models[id] ? [models[id].x, models[id].y, models[id].z].map((v) => Math.round(v * 10) / 10) : null,
    })),
    parts: parts.map((id) => ({ name: name(id), hands: isLeftRight(items[Number(id)]) })),
    rows: merged.map(({ r, both }) => ({
      hardware: r.hardware.map(name), covers: r.covers.map(name), parts: r.components.map(name),
      how: r.descriptionEn || r.descriptionFr, both,
    })),
    holes: capping.map((h) => ({
      id: h.id, partName: name(h.part), ...hand(h.side), face: h.face, u: atWords(h.u), v: atWords(h.v),
      hardware: h.via != null ? name(h.via) : null, entry: isEntry(h), through: h.through,
      // HOW DEEP IT IS AT ITS MOUTH: where the wood begins behind the face, the bore's width, and
      // the recess its head sits in, where Find holes found one.
      mouthMm: h.surfaceMm ?? null, boreMm: holeWidthOf(h, items, models), recessMm: h.sunkMm ?? null,
    })),
    spots: spots.map((s) => ({
      id: s.id, partName: name(s.part), ...hand(s.side), face: s.face, u: atWords(s.u), v: atWords(s.v),
    })),
  };
}

const bodyOf = (facts: CoverFacts, model: string) => ({ ...facts, model });

export async function coverPlacementPrompt(facts: CoverFacts, model: string) {
  const { data } = await axios.post<{ prompt: string; model: string }>(
    '/api/notice/cover-placements', { ...bodyOf(facts, model), preview: true },
  );
  return data;
}

type Answer = {
  placements: Placement[];
  notes: string; reasoning: string; raw: string;
};
const ask = async (facts: CoverFacts, model: string, signal?: AbortSignal) => {
  const { data } = await axios.post<Answer>('/api/notice/cover-placements', bodyOf(facts, model), { signal });
  // THE KEYS BACK TO STORED IDS.
  const placements = data.placements.flatMap((p) => {
    const id = facts.covers.find((c) => c.key === p.cover)?.id;
    return id == null ? [] : [{ ...p, cover: id }];
  });
  return { placements, pass: { reasoning: data.reasoning, raw: data.raw, notes: data.notes } };
};

// ONE PASS, for every cover of the joint — its buttons on their holes, its felt pads on the feet.
export async function runCoverPlacement(
  docId: string, joint: string, facts: CoverFacts, model: string,
  // STOP'S ABORT, from the Holes & covers run: the call out is dropped.
  signal?: AbortSignal,
): Promise<void> {
  if (store.get(docId).busy) return;
  // THE LAST ANSWER OFF THE MODEL WHILE THE NEXT IS ASKED: covers drawn during a run are covers
  // it is about to replace. Back on if the run fails, since then nothing replaced them.
  const was = store.get(docId).byJoint[joint] ?? NO_COVERS;
  store.patch(docId, { busy: joint, error: null });
  patchJoint(docId, joint, { placements: null });
  try {
    const first = await ask(facts, model, signal);
    patchJoint(docId, joint, { placements: first.placements, passes: [first.pass] });
    save(docId);
  } catch (e) {
    const got = axios.isAxiosError(e) ? (e.response?.data as { error?: string } | undefined)?.error : null;
    // Aborted by Stop is not a failure: the joint is being put back (see JointsRun).
    if (!axios.isCancel(e)) store.patch(docId, { error: got || String(e) });
    if (!store.get(docId).byJoint[joint]?.placements) patchJoint(docId, joint, { placements: was.placements });
  } finally {
    store.patch(docId, { busy: null });
  }
}
